import { Preview } from "@creatomate/preview";
import { v4 as uuid } from "uuid";

import { ElementSource, Organization, Story, Template } from "chq";
import getText from "components/editor/getText";

import { IMAGE_TYPE } from "../constants";
import sourceToSnakeCase from "./sourceToSnakeCase";
import { findAllGenericElements } from "../../sidebar/tabs/genericTemplate/utils/findGenericElementByName";
import getTimeDifference from "../../sidebar/tabs/template/implementors/utils/getTimeDifference";
import { SelectedMedia } from "../../sidebar/tabs/template/implementors/genericMontageTemplate";
import getMontageVideoUrl from "../../sidebar/tabs/template/implementors/utils/getMontageVideoUrl";

interface NewStoryElement {
  id: string;
  selectedVisual: SelectedMedia;
  source: ElementSource;
}

interface ElementUpdate {
  id?: string;
  updates?: Partial<ElementSource>;
}

const newRelativeElement = (
  relativeElement: ElementSource,
  newStoryElementId: string,
  newStoryElement: ElementSource,
  storyNewTime: number
) => ({
  ...relativeElement,
  name: relativeElement.name.replace(/RT=[^@]+/, `RT=${newStoryElementId}`),
  time: storyNewTime + (relativeElement.time - newStoryElement.source.time),
  id: uuid()
});

const storyElement = (
  newStoryElement: ElementSource,
  newStoryElementId: string,
  storyNewTime: number,
  duration: number,
  organization: Organization
) => ({
  ...newStoryElement.source,
  id: newStoryElementId,
  source: getMontageVideoUrl(newStoryElement.selectedVisual.media, organization),
  type: newStoryElement.selectedVisual.media.mediaType,
  time: storyNewTime,
  duration
});

const getRTValue = (input?: string): string | null => {
  if (!input) return null;
  const match = input.match(/RT=([^@]+)/);
  return match ? match[1] : null;
};

const createNewStoryElement = (
  templateElement: ElementSource,
  selectedVisual: SelectedMedia
): NewStoryElement => ({
  id: templateElement.id,
  selectedVisual,
  source: templateElement
});

const addStoryToMontageTemplate = async (
  template: Template,
  preview: Preview,
  story: Story,
  organization: Organization
): Promise<string | undefined> => {
  const templateSource = sourceToSnakeCase(template.creatomateJson);
  const templateElements = templateSource?.elements || [];

  const {
    storyElement: templateStoryElements,
    introElement: templateIntroElements
  } = findAllGenericElements(templateElements);
  const sortedTemplateIntroElements = [...templateIntroElements].sort((a, b) => (
    (a.time + a.duration) - (b.time + b.duration)
  ));
  const sortedTemplateStoryElements = [...templateStoryElements].sort((a, b) => a.time - b.time);

  const lastIntroElement = sortedTemplateIntroElements.reduce((max, current) => (
    (current.time + current.duration) > (max.time + max.duration) ? current : max
  ), sortedTemplateIntroElements[0]);
  const timeDifference = getTimeDifference(sortedTemplateStoryElements, lastIntroElement);

  const source = preview.getSource();
  const elements: ElementSource[] = source?.elements || [];
  const { storyElement: storyElements } = findAllGenericElements(elements);
  const sortedStoryElements = [...storyElements].sort((a, b) => a.time - b.time);
  const lastStoryElement = sortedStoryElements.reduce((max, current) => (
    current.time > max.time ? current : max
  ), sortedStoryElements[0]);
  const storiesEndTime = (lastStoryElement?.time ?? 0) + (lastStoryElement?.duration ?? 0);

  const selectedMedia: SelectedMedia[] = story.visuals?.map(
    visual => ({ media: visual, story })
  ) || [];

  const numberOfSelectedStories = storyElements.length;
  let indexToUse = numberOfSelectedStories % sortedTemplateStoryElements.length;
  const newStoryElements = selectedMedia.map(selectedVisual => {
    const element = createNewStoryElement(
      templateStoryElements[indexToUse % templateStoryElements.length],
      selectedVisual
    );
    indexToUse += 1;
    return element;
  }) || [];

  let currentTime = lastStoryElement.time + lastStoryElement.duration;
  const storiesMap: ElementSource = {};
  const newElements = newStoryElements.map(newStoryElement => {
    const relativeElements = templateElements.filter((element: ElementSource) => (
      element.name.includes(newStoryElement.id)
    ));
    const newStoryElementId = uuid();
    const duration = newStoryElement.selectedVisual.media.mediaType === IMAGE_TYPE
      ? 5 : (newStoryElement.selectedVisual.media.duration ?? 0);
    const storyNewTime = currentTime + timeDifference[newStoryElement.id];

    storiesMap[newStoryElementId] = newStoryElement.selectedVisual;
    if (!relativeElements.length) {
      const result = [
        storyElement(newStoryElement, newStoryElementId, storyNewTime, duration, organization)
      ];
      currentTime += timeDifference[newStoryElement.id] + duration;
      return result;
    }
    const result = [
      ...relativeElements.map((relativeElement: ElementSource) => (
        newRelativeElement(relativeElement, newStoryElementId, newStoryElement, storyNewTime)
      )),
      storyElement(newStoryElement, newStoryElementId, storyNewTime, duration, organization)
    ];
    currentTime += timeDifference[newStoryElement.id] + duration;
    return result;
  });

  const updatedElements = [...elements, ...newElements.flat()];

  const {
    captionElement,
    creatorFirstNameElement,
    creatorLastnameElement,
    creatorNameElement,
    creatorTitleElement,
    creatorDeparmentElement,
    creatorProfessionElement,
    creatorBusinessElement,
    creatorProfileImageElement,
    creatorLocationElement,
    nameAndTitleElement,
    promptElement,
    trendElement,
    outroElement,
    storyElement: updatedStoryElements
  } = findAllGenericElements(updatedElements);

  const newSortedStoryElements = [...updatedStoryElements].sort((a, b) => a.time - b.time);
  const newLastStoryElement = newSortedStoryElements.reduce((max, current) => (
    current.time > max.time ? current : max
  ), newSortedStoryElements[0]);

  const elementUpdates: ElementUpdate[] = [
    ...creatorNameElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) return {};

      return {
        id: element.id,
        updates: {
          text: storiesMap[storyElementId].story.creator.name
        }
      };
    }),
    ...nameAndTitleElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) return {};

      return {
        id: element.id,
        updates: {
          text: `${story.creator.name}\n${story.creator.title || ""}`
        }
      };
    }),
    ...creatorLocationElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) return {};

      return {
        id: element.id,
        updates: {
          text: story.creator.location?.name || ""
        }
      };
    }),
    ...creatorTitleElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) return {};

      return {
        id: element.id,
        updates: {
          text: story.creator.title || ""
        }
      };
    }),
    ...creatorDeparmentElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) return {};

      return {
        id: element.id,
        updates: {
          text: story.creator.departments?.[0]?.name || ""
        }
      };
    }),
    ...promptElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) return {};

      return {
        id: element.id,
        updates: {
          text: story.question.question || ""
        }
      };
    }),
    ...trendElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) return {};

      return {
        id: element.id,
        updates: {
          text: story.question?.parentStoryQuestion?.question || ""
        }
      };
    }),
    ...captionElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) return {};

      return {
        id: element.id,
        updates: {
          text: getText(story.body || "")
        }
      };
    }),
    ...creatorFirstNameElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) return {};

      return {
        id: element.id,
        updates: {
          text: story.creator.firstName || story.creator?.name || ""
        }
      };
    }),
    ...creatorLastnameElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) return {};

      return {
        id: element.id,
        updates: {
          text: story.creator.lastName || story.creator?.name || ""
        }
      };
    }),
    ...creatorTitleElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) return {};

      return {
        id: element.id,
        updates: {
          text: story.creator.title || ""
        }
      };
    }),
    ...creatorProfessionElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) return {};

      return {
        id: element.id,
        updates: {
          text: story.creator.departments?.[0]?.name || ""
        }
      };
    }),
    ...creatorBusinessElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) return {};

      return {
        id: element.id,
        updates: {
          text: story.creator.departments?.[0]?.name || ""
        }
      };
    }),
    ...creatorProfileImageElement.map(element => {
      const storyElementId = getRTValue(element.name);
      if (!storyElementId || !storiesMap[storyElementId]) return {};

      return {
        id: element.id,
        updates: {
          source: story.creator?.avatar?.thumbUrl || "",
          name: `${story.creator?.name}'s photo`
        }
      };
    }),
    ...outroElement.map(element => {
      const outroDiff = (element?.time ?? 0) - storiesEndTime;
      return {
        id: element.id,
        updates: {
          time: (newLastStoryElement?.time ?? 0) + (newLastStoryElement?.duration ?? 0) + outroDiff
        }
      };
    })
  ];

  const elementMappings = elementUpdates
    .filter((update): update is ElementUpdate & { id: string } => Boolean(update.id))
    .reduce((acc, { id, updates }) => {
      acc[id] = { ...acc[id], ...updates };
      return acc;
    }, {} as Record<string, Partial<ElementSource>>);

  const finalElements = updatedElements.map((element: ElementSource) => ({
    ...element,
    ...elementMappings[element.id]
  }));

  const lastElement = finalElements.reduce((max, current) => (
    (current.time + current.duration) > (max.time + max.duration) ? current : max
  ), updatedElements[0]);

  const totalDuration = lastElement.time + lastElement.duration;
  await preview.setSource({
    ...source,
    elements: finalElements,
    duration: totalDuration
  });

  return newStoryElements[0].id;
};

export default addStoryToMontageTemplate;
